import { useState } from 'react';

// ============================================
// CONFIGURATION - UPDATE THIS
// ============================================
const ZAPIER_WEBHOOK_URL = 'https://hooks.zapier.com/hooks/catch/25467745/ul0q0uk/';
const SUBSTACK_URL = 'https://davidkasteler.substack.com/subscribe';
// ============================================

const sections = [
  {
    id: 'physical',
    title: 'Physical',
    subtitle: 'Body, physical well-being, bodily function',
    questions: [
      'Stretched or done yoga for five or more minutes?',
      'Exercised for 20 or more minutes?',
      'Consumed at least eight glasses of water?',
      'Avoided sugars and processed foods?',
      'Fasted intentionally for 14-18 hours?',
      'Felt grateful for your body?',
      'Made a meal plan with colorful, nutritious foods?',
      'Slept for seven or more hours?',
      'Woken up feeling rested and energized?',
      'Taken medications (prescription or over the counter)?'
    ],
    reversed: [9]
  },
  {
    id: 'intellectual',
    title: 'Intellectual',
    subtitle: 'Brain power, thoughts, learning, reasoning',
    questions: [
      'Read or listened to a book?',
      'Intentionally memorized something new?',
      'Studied a foreign language?',
      'Written in a journal or done reflective writing?',
      'Played a musical instrument?',
      'Attended a class or followed an online tutorial?',
      'Engaged in a mentally challenging puzzle or game?',
      'Had a substantive conversation about ideas?',
      'Learned something that changed your perspective?',
      'Had a "digital getaway" with 8+ hours away from devices?'
    ],
    reversed: []
  },
  {
    id: 'emotional',
    title: 'Emotional',
    subtitle: 'Feelings, inner thoughts, emotional regulation',
    questions: [
      'Written a letter or heartfelt message to someone?',
      'Processed difficult emotions in a healthy way?',
      'Volunteered or intentionally helped someone else?',
      'Intentionally limited your time on social media?',
      'Cared for a pet or plant?',
      'Felt genuinely excited about life?',
      'Sung a song or hummed a tune aloud?',
      'Expressed your feelings openly to someone you trust?',
      'Practiced self-compassion during a difficult moment?',
      'Done something creative (art, writing, music, cooking)?'
    ],
    reversed: []
  },
  {
    id: 'spiritual',
    title: 'Spiritual',
    subtitle: 'Connection to something greater than yourself',
    questions: [
      'Spent 20 minutes or more intentionally outdoors?',
      'Meditated or practiced mindfulness?',
      'Prayed or engaged in spiritual practice?',
      'Written in a gratitude journal?',
      'Read and pondered ancient or sacred texts?',
      'Learned about your family history or heritage?',
      'Practiced yoga or breathwork?',
      'Listened to uplifting or meaningful music?',
      'Intentionally forgave or blessed others?',
      'Felt a sense of awe or wonder about life?'
    ],
    reversed: []
  },
  {
    id: 'social',
    title: 'Social',
    subtitle: 'Relationships and social connections',
    questions: [
      'Met a friend in person?',
      'Been intentional about your social media use?',
      'Browsed social media unintentionally (scrolling)?',
      'Talked 10+ minutes with friend or family (phone/video)?',
      'Been active in a group activity?',
      'Nurtured an important relationship in your life?',
      'Told someone that you love or care for them?',
      'Laughed out loud with friends or family?',
      'Introduced yourself to someone new?',
      'Actively listened to someone without interrupting?'
    ],
    reversed: [2]
  },
  {
    id: 'occupational',
    title: 'Occupational',
    subtitle: 'Work, career, purposeful contribution',
    questions: [
      'Actively focused on improving your work output?',
      'Read something to help improve your work?',
      'Been on time for work and meetings?',
      'Felt engaged with your tasks, even challenging ones?',
      'Set occupational or professional goals?',
      'Felt that your work matters?',
      'Looked for ways to improve your attitude?',
      'Planned your meals and snacks for work days?',
      'Made a list of your top 5-7 priorities for the day?',
      'Planned intentional focus time in 30-45 min blocks?'
    ],
    reversed: []
  }
];

const freqOpts = [
  { label: 'Nearly every day', value: 3 },
  { label: 'More than half', value: 2 },
  { label: 'Several days', value: 1 },
  { label: 'Not at all', value: 0 }
];

const getZone = (s) => {
  if (s >= 21) return { zone: 'Green', label: 'Strong Foundation', color: 'bg-green-500', txt: 'text-green-700', bg: 'bg-green-50' };
  if (s >= 11) return { zone: 'Yellow', label: 'Room for Growth', color: 'bg-yellow-500', txt: 'text-yellow-700', bg: 'bg-yellow-50' };
  return { zone: 'Red', label: 'Needs Attention', color: 'bg-red-500', txt: 'text-red-700', bg: 'bg-red-50' };
};

const getTotalZone = (s) => {
  if (s >= 126) return { zone: 'Green', label: 'Thriving' };
  if (s >= 66) return { zone: 'Yellow', label: 'Building' };
  return { zone: 'Red', label: 'Foundation Needed' };
};

export default function App() {
  const [curSec, setCurSec] = useState(0);
  const [ans, setAns] = useState({});
  const [view, setView] = useState('quiz');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const handleAns = (secId, qIdx, val, rev) => {
    const actual = rev ? (3 - val) : val;
    setAns(p => ({ ...p, [`${secId}-${qIdx}`]: actual }));
  };

  const getSecScore = (secId) => {
    let score = 0;
    for (let i = 0; i < 10; i++) score += ans[`${secId}-${i}`] || 0;
    return score;
  };

  const getTotal = () => sections.reduce((t, s) => t + getSecScore(s.id), 0);

  const isSecDone = (secId) => {
    for (let i = 0; i < 10; i++) if (ans[`${secId}-${i}`] === undefined) return false;
    return true;
  };

  const allDone = sections.every(s => isSecDone(s.id));

  const handleSubmitEmail = async () => {
    if (!email.includes('@') || !email.includes('.')) {
      setError('Please enter a valid email address');
      return;
    }

    setSending(true);
    setError('');

    const total = getTotal();
    const tZone = getTotalZone(total);

    const payload = {
      email: email,
      totalScore: total,
      totalZone: tZone.zone + ' - ' + tZone.label,
      physical: getSecScore('physical'),
      intellectual: getSecScore('intellectual'),
      emotional: getSecScore('emotional'),
      spiritual: getSecScore('spiritual'),
      social: getSecScore('social'),
      occupational: getSecScore('occupational'),
      date: new Date().toISOString()
    };

    try {
      await fetch(ZAPIER_WEBHOOK_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      setView('confirmation');
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // CONFIRMATION SCREEN (after email submit)
  if (view === 'confirmation') {
    return (
      <div className="max-w-xl mx-auto p-8 bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Check Your Inbox!</h2>
          <p className="text-gray-600 mb-8">
            Your personalized BaseLift results are on their way to <strong>{email}</strong>
          </p>
          
          <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 mb-6">
            <h3 className="font-semibold text-gray-800 mb-2">While you wait...</h3>
            <p className="text-gray-600 text-sm mb-4">
              Subscribe to the BaseLift newsletter for weekly insights on cellular health and longevity.
            </p>
            <a 
              href={SUBSTACK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-6 py-3 bg-amber-400 hover:bg-amber-500 text-black font-semibold rounded-lg transition-colors"
            >
              Subscribe to BaseLift →
            </a>
          </div>

          <button 
            onClick={() => { setView('quiz'); setAns({}); setCurSec(0); setEmail(''); }}
            className="text-gray-500 hover:text-gray-700 text-sm underline"
          >
            Take the assessment again
          </button>
        </div>
      </div>
    );
  }

  // EMAIL CAPTURE SCREEN
  if (view === 'email') {
    return (
      <div className="max-w-md mx-auto p-6 bg-white min-h-screen flex items-center justify-center">
        <div className="w-full">
          <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">You're Done!</h2>
          <p className="text-gray-600 mb-6 text-center">
            Enter your email to receive your personalized BaseLift results and recommendations.
          </p>
          
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}
          
          <input 
            type="email" 
            value={email} 
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
            onKeyPress={(e) => e.key === 'Enter' && handleSubmitEmail()}
          />
          
          <button 
            onClick={handleSubmitEmail}
            disabled={sending}
            className="w-full py-3 bg-amber-400 hover:bg-amber-500 text-black rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? 'Sending...' : 'Send My Results'}
          </button>
          
          <p className="text-xs text-gray-400 mt-4 text-center">
            We respect your privacy. Unsubscribe anytime.
          </p>
        </div>
      </div>
    );
  }

  // QUIZ SCREEN
  const sec = sections[curSec];

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white">
      {/* Progress */}
      <div className="mb-6">
        <div className="flex justify-between text-sm text-gray-500 mb-2">
          <span>Section {curSec + 1} of 6</span>
          <span>{sec.title}</span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-amber-400 transition-all duration-300" 
            style={{ width: `${((curSec + 1) / 6) * 100}%` }}
          />
        </div>
      </div>

      {/* Section Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">{sec.title}</h2>
        <p className="text-gray-500">{sec.subtitle}</p>
        <p className="text-sm text-gray-400 mt-2">Over the last 4 weeks, how often have you...</p>
      </div>

      {/* Questions */}
      <div className="space-y-4">
        {sec.questions.map((q, qi) => {
          const rev = sec.reversed.includes(qi);
          const cur = ans[`${sec.id}-${qi}`];
          return (
            <div key={qi} className="p-4 bg-gray-50 rounded-lg">
              <p className="font-medium text-gray-700 mb-3">{qi + 1}. {q}</p>
              <div className="grid grid-cols-2 gap-2">
                {freqOpts.map((o, oi) => {
                  const dVal = rev ? (3 - o.value) : o.value;
                  const sel = cur === dVal;
                  return (
                    <button
                      key={oi}
                      onClick={() => handleAns(sec.id, qi, o.value, rev)}
                      className={`py-2 px-3 rounded text-sm transition-colors ${
                        sel 
                          ? 'bg-amber-400 text-black font-medium' 
                          : 'bg-white border border-gray-200 text-gray-600 hover:border-amber-300'
                      }`}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Navigation */}
      <div className="flex gap-4 mt-8">
        {curSec > 0 && (
          <button 
            onClick={() => setCurSec(p => p - 1)}
            className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
          >
            Previous
          </button>
        )}
        {curSec < 5 ? (
          <button 
            onClick={() => setCurSec(p => p + 1)}
            disabled={!isSecDone(sec.id)}
            className={`flex-1 py-3 rounded-lg font-semibold transition-colors ${
              isSecDone(sec.id) 
                ? 'bg-amber-400 text-black hover:bg-amber-500' 
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            Next Section
          </button>
        ) : (
          <button 
            onClick={() => setView('email')}
            disabled={!allDone}
            className={`flex-1 py-3 rounded-lg font-semibold transition-colors ${
              allDone 
                ? 'bg-amber-400 text-black hover:bg-amber-500' 
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            Get My Results
          </button>
        )}
      </div>

      {/* Section dots */}
      <div className="flex justify-center gap-2 mt-6">
        {sections.map((s, i) => (
          <button
            key={s.id}
            onClick={() => setCurSec(i)}
            className={`w-3 h-3 rounded-full transition-colors ${
              i === curSec 
                ? 'bg-amber-400' 
                : isSecDone(s.id) 
                  ? 'bg-amber-200' 
                  : 'bg-gray-300'
            }`}
          />
        ))}
      </div>
    </div>
  );
}